import type { Middleware, Placement, Strategy, UseHoverProps } from "@floating-ui/react";
import {
	arrow as arrowFunc,
	autoPlacement as auto,
	autoUpdate,
	FloatingArrow,
	FloatingPortal,
	offset as offsetFunc,
	safePolygon,
	shift,
	useDismiss,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import { type HTMLMotionProps, motion, type Transition } from "motion/react";
import { cloneElement, type JSX, type ReactNode, useMemo, useRef, useState } from "react";

import { cn } from "./lib/utils";

// Tooltip variant type
type TooltipVariantKey = "default" | "subtle" | "popover";

// Combined style and animation config for each variant
const tooltipVariantConfig: Record<
	TooltipVariantKey,
	{
		className: string;
		arrowClassName: string;
		animation: {
			initial: Record<string, number>;
			animate: Record<string, number>;
			exit: Record<string, number>;
			transition: Transition;
		};
	}
> = {
	// Dark background with spring scale animation
	default: {
		className:
			"w-max max-w-[min(calc(100vw-1rem),20rem)] whitespace-normal break-words rounded-md px-2 py-1.5 text-sm z-50 bg-gray-800 text-white dark:bg-gray-100 dark:text-gray-900",
		arrowClassName: "fill-gray-800 dark:fill-gray-100",
		animation: {
			initial: { opacity: 0, scale: 0.85 },
			animate: { opacity: 1, scale: 1 },
			exit: { opacity: 0 },
			transition: {
				type: "spring",
				damping: 20,
				stiffness: 300,
			},
		},
	},
	// Subtle muted style with simple fade
	subtle: {
		className:
			"w-max max-w-[min(calc(100vw-1rem),20rem)] whitespace-normal break-words rounded-md border border-border bg-muted px-2 py-1.5 text-sm z-50 text-muted-foreground shadow-sm",
		arrowClassName: "fill-muted",
		animation: {
			initial: { opacity: 0 },
			animate: { opacity: 1 },
			exit: { opacity: 0 },
			transition: {
				duration: 0.15,
				ease: "easeOut",
			},
		},
	},
	// Popover-like style with shift animation
	popover: {
		className:
			"w-max max-w-[min(calc(100vw-1rem),20rem)] whitespace-normal break-words rounded-md border bg-popover px-2 py-1.5 text-sm z-50 text-popover-foreground shadow-md",
		arrowClassName: "fill-popover",
		animation: {
			initial: { opacity: 0, y: 4 },
			animate: { opacity: 1, y: 0 },
			exit: { opacity: 0, y: 2 },
			transition: {
				duration: 0.2,
				ease: "easeOut",
			},
		},
	},
};

export type TooltipVariant = TooltipVariantKey;

export interface TooltipProps extends Omit<HTMLMotionProps<"div">, "content"> {
	content: ReactNode;
	children: JSX.Element;
	placement?: Placement | Placement[];
	disabled?: boolean;
	delay?: number;
	offset?: number;
	mouseOnly?: boolean;
	arrow?: boolean;
	strategy?: Strategy;
	handleClose?: UseHoverProps["handleClose"];
	restMs?: number;
	hoverEnabled?: boolean;
	/** Style and animation variant for the tooltip */
	variant?: TooltipVariant;
}

export const Tooltip = ({
	content,
	placement,
	disabled = false,
	delay = 0,
	offset = 5,
	mouseOnly = true,
	restMs = 40,
	strategy = "fixed",
	handleClose = safePolygon(),
	hoverEnabled = true,
	children,
	arrow,
	variant = "default",
	className,
	...props
}: TooltipProps) => {
	const [open, setOpen] = useState<boolean>(false);
	const arrowRef = useRef(null);
	const middleware = [
		offsetFunc(offset),
		shift({ padding: 8 }),
		auto({
			allowedPlacements: Array.isArray(placement)
				? placement
				: typeof placement === "string"
					? [placement]
					: undefined,
		}),
		arrow ? arrowFunc({ element: arrowRef.current }) : undefined,
	].filter((s) => !!s) as Middleware[];
	const {
		x,
		y,
		strategy: floatingStrategy,
		refs,
		context,
	} = useFloating({
		open: open && !disabled,
		onOpenChange: setOpen,
		middleware,
		whileElementsMounted: autoUpdate,
		strategy,
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([
		useHover(context, {
			delay,
			restMs,
			mouseOnly,
			handleClose,
			enabled: hoverEnabled,
		}),
		useFocus(context),
		useRole(context, { role: "tooltip" }),
		useDismiss(context),
	]);

	// Get variant config (default to 'default' animation if no variant specified)
	const variantConfig = variant ? tooltipVariantConfig[variant] : tooltipVariantConfig.default;

	// Determine content to render
	const tooltipContent = useMemo(() => {
		// If variant is specified and content is a string, wrap it with styled container
		if (variant !== undefined && typeof content === "string") {
			return <span className={cn(variantConfig.className, className)}>{content}</span>;
		}
		// Otherwise render content as-is
		return content;
	}, [variant, content, className, variantConfig.className]);

	const Float = (
		<motion.div
			initial={variantConfig.animation.initial}
			animate={variantConfig.animation.animate}
			exit={variantConfig.animation.exit}
			transition={variantConfig.animation.transition}
			{...getFloatingProps({
				ref: refs.setFloating,
				className: className,
				style: {
					position: floatingStrategy,
					top: y ?? 0,
					left: x ?? 0,
				},
			})}
			{...props}
		>
			{arrow && (
				<FloatingArrow
					ref={arrowRef}
					context={context}
					className={
						variant ? variantConfig.arrowClassName : "fill-gray-800 dark:fill-gray-100"
					}
				/>
			)}
			{tooltipContent}
		</motion.div>
	);

	return (
		<>
			{children &&
				cloneElement(
					children,
					getReferenceProps({
						ref: refs.setReference,
						...children.props,
					}),
				)}
			{open && !disabled && <FloatingPortal>{Float}</FloatingPortal>}
		</>
	);
};
